import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "Agenda", path: "/agenda" },
  { label: "Speakers", path: "/speakers" },
  { label: "Expo", path: "/expo" },
  { label: "Register", path: "/register" },
  { label: "Sponsor", path: "/sponsor" },
  { label: "Contact", path: "/contact" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubStatus("success");
      setEmail("");
      setTimeout(() => setSubStatus(""), 3000);
    } catch {
      setSubStatus("error");
      setTimeout(() => setSubStatus(""), 3000);
    }
  };

  return (
    <footer data-testid="footer" className="bg-forest-core text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Col 1: Logo + Tagline */}
          <div>
            <Link to="/" className="block">
              <span className="text-lemon-beam font-extrabold text-xl tracking-tight leading-tight">
                BioResources & Circular Economy
              </span>
              <span className="block text-white font-bold text-sm tracking-tight mt-0.5">
                Summit & Expo 2026
              </span>
            </Link>
            <p className="mt-4 text-lime-mist text-sm leading-relaxed">
              Where Waste Becomes Value.<br />Where Science Meets Commerce.
            </p>
            <p className="mt-3 text-muted-text text-xs">
              28 September 2026 &middot; The Westin Mumbai Powai Lake
            </p>
          </div>

          {/* Col 2: Quick Links */}
          <div>
            <h4 className="text-lemon-beam font-bold text-sm uppercase tracking-[0.15em] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.label.toLowerCase()}-link`}
                    className="text-white/70 text-sm hover:text-lemon-beam transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Contact */}
          <div>
            <h4 className="text-lemon-beam font-bold text-sm uppercase tracking-[0.15em] mb-4">
              Contact
            </h4>
            <div className="space-y-3 text-sm text-white/70">
              <p>events@teflas.com</p>
              <p>Helpline: +91 98338 55566</p>
              <p>+91 98337 55566</p>
              <p className="mt-3 text-xs leading-relaxed">
                Tefla's,<br />
                301 AB & 308, Abhishek Premises,<br />
                C-5 Dalia Industrial Estate,<br />
                Off New Link Road, Andheri (W),<br />
                Mumbai 400053
              </p>
            </div>
          </div>

          {/* Col 4: Newsletter + Social */}
          <div>
            <h4 className="text-lemon-beam font-bold text-sm uppercase tracking-[0.15em] mb-4">
              Newsletter
            </h4>
            <p className="text-white/70 text-sm mb-3">
              Stay updated on speakers, programme and early bird offers.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                data-testid="footer-newsletter-email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 text-white text-sm px-3 py-2 rounded-[8px] placeholder:text-white/40 focus:outline-none focus:border-lemon-beam"
                required
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="bg-lemon-beam text-forest-core font-bold text-sm px-4 py-2 rounded-[8px] hover:bg-gold transition-colors duration-200 flex items-center gap-1"
              >
                <ArrowRight size={16} />
              </button>
            </form>
            {subStatus === "success" && (
              <p data-testid="newsletter-success" className="text-lime-mist text-xs mt-2">Subscribed successfully!</p>
            )}
            {subStatus === "error" && (
              <p data-testid="newsletter-error" className="text-red-400 text-xs mt-2">Something went wrong. Try again.</p>
            )}

            <div className="flex gap-4 mt-6">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin" className="text-white/50 hover:text-lemon-beam transition-colors">
                <Linkedin size={20} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" data-testid="social-twitter" className="text-white/50 hover:text-lemon-beam transition-colors">
                <Twitter size={20} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-white/50 hover:text-lemon-beam transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/40">
          <p>&copy; Tefla's 2026. All Rights Reserved.</p>
          <div className="flex gap-4">
            <span>Privacy Policy</span>
            <span>Terms & Conditions</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
